import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Download, Upload, Trash2, ShieldAlert, Database, History, FileJson, RotateCcw, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/format';
import { Badge } from '../components/ui/badge';

const DataManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = hasRole(user, 'super_admin');
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState('');

  // Purge dialog state
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePassword, setPurgePassword] = useState('');
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeError, setPurgeError] = useState('');

  // Restore dialog state
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);

  const loadBackups = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const r = await api.get('/admin/backups');
      setBackups(r.data);
    } catch {}
  }, [isSuperAdmin]);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  const downloadBackup = async () => {
    setBusy('backup');
    try {
      const r = await api.post('/admin/backup', null, { responseType: 'blob' });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mpms_backup_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Backup downloaded', description: 'Save this file in a safe place.' });
      loadBackups();
    } catch (e) {
      toast({ title: 'Backup failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const onRestoreFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.json')) {
      toast({ title: 'Invalid file', description: 'Only .json backup files are accepted', variant: 'destructive' });
      return;
    }
    setRestoreFile(f);
  };

  const submitRestore = async () => {
    if (!restoreFile) return;
    if (!window.confirm('Restoring will REPLACE all current data with the backup content. Continue?')) return;
    setBusy('restore');
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      const r = await api.post('/admin/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const totals = Object.entries(r.data.restored || {}).reduce((s, [, n]) => s + n, 0);
      toast({ title: 'Restore complete', description: `Restored ${totals} records across ${Object.keys(r.data.restored || {}).length} collections.` });
      setRestoreOpen(false);
      setRestoreFile(null);
      loadBackups();
    } catch (e) {
      toast({ title: 'Restore failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const submitPurge = async () => {
    setPurgeError('');
    if (purgeConfirm !== 'PURGE') {
      setPurgeError('Type PURGE in the confirmation field to proceed.');
      return;
    }
    if (!purgePassword) {
      setPurgeError('Password is required.');
      return;
    }
    setBusy('purge');
    try {
      const r = await api.post('/admin/purge', { password: purgePassword });
      const total = Object.values(r.data.counts || {}).reduce((s, n) => s + n, 0);
      toast({ title: 'Purge complete', description: `Removed ${total} records. Only super.admin preserved.` });
      setPurgeOpen(false);
      setPurgePassword('');
      setPurgeConfirm('');
    } catch (e) {
      setPurgeError(e.response?.data?.detail || 'Purge failed');
    } finally { setBusy(''); }
  };

  if (!isSuperAdmin) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Only Super Admin can access Data Management.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center"><Download className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900">Backup Data</h3>
              <p className="text-xs text-slate-500 mt-1">Download a complete JSON snapshot of all collections. Save it externally to retrieve later.</p>
            </div>
            <Button onClick={downloadBackup} disabled={busy === 'backup'} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {busy === 'backup' ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating...</> : <><Download className="h-4 w-4 mr-1.5" /> Download Backup</>}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="h-11 w-11 rounded-xl bg-sky-50 text-sky-700 grid place-items-center"><RotateCcw className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900">Restore from Backup</h3>
              <p className="text-xs text-slate-500 mt-1">Upload a previously downloaded JSON backup to restore all data. Replaces current content.</p>
            </div>
            <Button variant="outline" onClick={() => { setRestoreFile(null); setRestoreOpen(true); }} className="w-full border-sky-300 hover:bg-sky-50">
              <Upload className="h-4 w-4 mr-1.5" /> Restore Backup
            </Button>
          </CardContent>
        </Card>

        <Card className="border-red-200">
          <CardContent className="p-5 space-y-3">
            <div className="h-11 w-11 rounded-xl bg-red-50 text-red-700 grid place-items-center"><ShieldAlert className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900">Purge All Data</h3>
              <p className="text-xs text-slate-500 mt-1">Permanently delete all data. Super Admin account is always preserved. Password required.</p>
            </div>
            <Button variant="destructive" onClick={() => { setPurgePassword(''); setPurgeConfirm(''); setPurgeError(''); setPurgeOpen(true); }} className="w-full">
              <Trash2 className="h-4 w-4 mr-1.5" /> Purge Data
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Backup history */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><History className="h-4 w-4" /> Backup History</div>
            <span className="text-xs text-slate-500">{backups.length} backup{backups.length !== 1 ? 's' : ''}</span>
          </div>
          {backups.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Database className="h-8 w-8 mx-auto text-slate-300" />
              <div className="mt-2">No backups generated yet. Click "Download Backup" to create your first snapshot.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-left text-slate-600">
                  <tr>
                    <th className="p-3">Created</th>
                    <th className="p-3">Created By</th>
                    <th className="p-3">Size</th>
                    <th className="p-3">Records</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {backups.map((b) => {
                    const total = Object.values(b.counts || {}).reduce((s, n) => s + n, 0);
                    return (
                      <tr key={b.id} className="table-row-hover">
                        <td className="p-3 text-xs">{formatDateTime(b.created_at)}</td>
                        <td className="p-3 text-xs font-mono">{b.created_by}</td>
                        <td className="p-3 text-xs">{(b.size_bytes / 1024).toFixed(1)} KB</td>
                        <td className="p-3">
                          <Badge variant="secondary" className="text-xs">{total} total</Badge>
                          <div className="text-[10px] text-slate-500 mt-1 truncate max-w-md">
                            {Object.entries(b.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join(' · ') || 'empty'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Purge dialog */}
      <Dialog open={purgeOpen} onOpenChange={(o) => { if (busy !== 'purge') setPurgeOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert className="h-5 w-5" /> Confirm Data Purge</DialogTitle>
            <DialogDescription>This action is permanent. All vehicles, operators, requests, inspections, mobilizations, equipment queues, maintenance, contracts, billings, purchase requests, consumable logs, dropdowns, notifications, login history, and non-super-admin users will be deleted. Your Super Admin account is preserved.</DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Tip:</strong> Download a backup first if you might want to restore later.
            </AlertDescription>
          </Alert>
          <div className="space-y-3">
            <div>
              <Label>Type <span className="font-mono font-bold">PURGE</span> to confirm</Label>
              <Input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} placeholder="PURGE" autoComplete="off" />
            </div>
            <div>
              <Label>Your password</Label>
              <Input type="password" value={purgePassword} onChange={(e) => setPurgePassword(e.target.value)} placeholder="Enter your Super Admin password" autoComplete="new-password" />
            </div>
            {purgeError && <Alert variant="destructive"><AlertDescription>{purgeError}</AlertDescription></Alert>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={busy === 'purge'}>Cancel</Button>
            <Button variant="destructive" onClick={submitPurge} disabled={busy === 'purge'}>
              {busy === 'purge' ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Purging...</> : <><Trash2 className="h-4 w-4 mr-1.5" /> Confirm Purge</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onOpenChange={(o) => { if (busy !== 'restore') setRestoreOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Restore from Backup</DialogTitle>
            <DialogDescription>Upload a previously generated <span className="font-mono">.json</span> backup file. Current data will be replaced.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50">
            <FileJson className="h-10 w-10 text-slate-400 mx-auto" />
            <div className="mt-3 text-sm text-slate-700">
              {restoreFile ? <span className="font-medium">{restoreFile.name} <span className="text-slate-500">({(restoreFile.size / 1024).toFixed(1)} KB)</span></span> : 'Choose your backup .json file'}
            </div>
            <label className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm font-medium hover:bg-slate-100 cursor-pointer">
              <FileJson className="h-4 w-4" /> Choose File
              <input type="file" accept=".json" className="hidden" onChange={onRestoreFile} />
            </label>
          </div>
          <Alert>
            <AlertDescription className="text-xs">
              Restoring will <strong>delete current data</strong> and replace it with the backup contents. Super Admin account is always preserved.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreOpen(false); setRestoreFile(null); }} disabled={busy === 'restore'}>Cancel</Button>
            <Button onClick={submitRestore} disabled={!restoreFile || busy === 'restore'} className="bg-sky-600 hover:bg-sky-700">
              {busy === 'restore' ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Restoring...</> : <><Upload className="h-4 w-4 mr-1.5" /> Restore Now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;
